// Phase 1B: read GET /api/tests/{id} and render:
//   - Test header: id, name (if any), track_code, source_event,
//     baseline setup name, KPI targets, provenance summary (if any).
//   - Run rail: every Run keyed by run_id, ordinal R{run_number},
//     source label, StatusChip from ./StatusChip, artifacts count.
//
// The view polls /api/tests/{id} at 2s cadence while ANY Run is in a
// non-terminal state (pending or running) so the engineer sees their
// prediction land without manual refresh. Polling stops once every
// Run is terminal — no wasted requests.
//
// The "Run prediction" / chart / provenance chip-row are Phase 1C work.
import { useCallback, useEffect, useRef, useState } from "react";

import type { StudyEvaluation } from "../api/evaluation";
import { fetchStudyEvaluation, guardrailVerdict } from "../api/evaluation";
import type { StudyInputsResponse } from "../api/studyInputs";
import { fetchStudyInputs } from "../api/studyInputs";
import type { StudyReport } from "../api/studyReport";
import { fetchStudyReport } from "../api/studyReport";
import type {
  DamperSettingPayload, KpiTarget, Run, Test,
} from "../api/tests";
import {
  cancelRun, createRun, deleteTest, fetchTest, rerunReference, updateTest,
} from "../api/tests";
import {
  estimateEtaSeconds, formatEtaSeconds, parseSimProgress,
  type SimObservation,
} from "./simProgress";
import { AutoResearchPanel } from "./AutoResearchPanel";
import { ComparisonPane } from "./ComparisonPane";
import { FrfPanel } from "./FrfPanel";
import { LiveStudyPanel } from "./LiveStudyPanel";
import { DamperEditSheet } from "./DamperEditSheet";
import { ResultsPane } from "./ResultsPane";
import { RunValidityDetail } from "./RunValidityDetail";
import { StatusChip } from "./StatusChip";
import { StudyReportPanel } from "./StudyReportPanel";
import { ParetoFrontView } from "./ParetoFrontView";
import type { ParetoFrontMember } from "../api/auto_research";
import { StudyDefinitionRail } from "./study/StudyDefinitionRail";

export type TestDetailViewProps = {
  testId: number;
  onClose: () => void;
  onTestUpdated?: (test: Test) => void;
  // Fired after the Test is hard-deleted so the parent can refetch the
  // list and close the detail pane.
  onDeleted?: () => void;
};

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; test: Test };

const POLL_INTERVAL_MS = 2000;
// Idle cadence: cheap GET keeping the view honest against server-side
// requeues (see the polling effect).
const IDLE_POLL_INTERVAL_MS = 10000;
const TERMINAL_STATUSES: Run["status"][] = ["complete", "failed", "cancelled"];

function hasNonTerminalRun(runs: Run[]): boolean {
  return runs.some((r) => !TERMINAL_STATUSES.includes(r.status));
}

/** SQLite datetime('now') text ("YYYY-MM-DD HH:MM:SS", UTC) → epoch ms.
 * Exported for tests. */
export function parseDbUtcMs(ts: string | null | undefined): number | null {
  if (!ts) return null;
  const iso = ts.includes("T") ? ts : `${ts.replace(" ", "T")}Z`;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

function formatElapsed(startMs: number, nowMs: number): string {
  const s = Math.max(0, Math.floor((nowMs - startMs) / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${(s % 60).toString().padStart(2, "0")}s` : `${s}s`;
}

/** The live one-liner under an in-flight Run card: elapsed time while
 * running, queue position while pending. Confidence line — the stage
 * text itself arrives via status_detail. Exported for tests. */
export function runLiveLine(run: Run, nowMs: number): string | null {
  if (run.status === "running") {
    const started = parseDbUtcMs(run.started_at);
    return started !== null
      ? `running · ${formatElapsed(started, nowMs)} elapsed`
      : "running";
  }
  if (run.status === "pending") {
    if (typeof run.queue_ahead === "number") {
      return run.queue_ahead === 0
        ? "queued · next up"
        : `queued · ${run.queue_ahead} ahead`;
    }
    return "queued";
  }
  return null;
}

/** Determinate progress bar + ETA for an integrating run. Renders only while
 * status_detail carries a "simulating <U+2014> X / Y s" pair; every other
 * stage string parses to null and this renders nothing (the plain
 * status-detail text stays the fallback). The ETA comes from the observed
 * sim-time rate and stays blank until two observations exist. */
function RunSimProgress({ run, nowMs }: { run: Run; nowMs: number }) {
  const parsed = parseSimProgress(run.status_detail);
  const obsRef = useRef<SimObservation[]>([]);
  const lastXRef = useRef<number | null>(null);
  useEffect(() => {
    if (!parsed) {
      obsRef.current = [];
      lastXRef.current = null;
      return;
    }
    // Record one observation per distinct sim-time so the rate estimate is
    // over wall-clock, not over render count.
    if (lastXRef.current === null || parsed.simS !== lastXRef.current) {
      obsRef.current.push({ tMs: nowMs, x: parsed.simS });
      lastXRef.current = parsed.simS;
      if (obsRef.current.length > 8) obsRef.current.shift();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed?.simS, nowMs]);

  if (!parsed) return null;
  const pct = Math.max(0, Math.min(100, (parsed.simS / parsed.durationS) * 100));
  const etaLabel = formatEtaSeconds(
    estimateEtaSeconds(obsRef.current, parsed.durationS),
  );
  return (
    <div className="run-sim-progress" data-testid={`run-sim-progress-${run.id}`}>
      <div
        className="run-sim-progress-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={Math.round(parsed.durationS)}
        aria-valuenow={Math.round(parsed.simS)}
      >
        <div
          className="run-sim-progress-fill"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="run-sim-progress-label">
        {parsed.simS.toFixed(1)} / {parsed.durationS.toFixed(1)} s
        {etaLabel ? ` · ${etaLabel}` : ""}
      </span>
    </div>
  );
}

function describeKpiTargets(targets: Test["kpi_targets"]): string {
  // Only entries shaped like a KPI target (direction + numeric target) are
  // objective constraints; other kpi_targets keys (track_replay, tire_override)
  // are input config, not objectives, and must not be rendered as "≥/≤".
  // Slice 4: the objective is chosen after the reference run, so a fresh Study
  // typically has none here.
  const kpiEntries = Object.entries(targets).filter(([, raw]) => {
    const v = raw as Partial<KpiTarget>;
    return (
      v != null &&
      typeof v === "object" &&
      (v.direction === "max" || v.direction === "min") &&
      typeof v.target === "number"
    );
  });
  if (kpiEntries.length === 0) return "objective set after the reference run";
  return kpiEntries
    .map(([k, raw]) => {
      const v = raw as KpiTarget;
      return `${k} ${v.direction === "max" ? "≥" : "≤"} ${v.target}`;
    })
    .join(" · ");
}

function describeRunIntent(run: Run): string {
  const setting = run.damper_setting as DamperSettingPayload | null | undefined;
  const corners = setting?.corners ?? {};
  const touched = Object.entries(corners)
    .filter(([, edit]) => edit && Object.keys(edit).length > 0)
    .map(([corner, edit]) => {
      const fields = [
        edit.damper_code ? "code" : null,
        edit.clicks ? "clicks" : null,
      ].filter(Boolean);
      return fields.length > 0
        ? `${corner.toUpperCase()} ${fields.join("+")}`
        : corner.toUpperCase();
    });
  if (touched.length === 0) return "baseline";
  return `edited ${touched.join(" · ")}`;
}

function describeRunRole(run: Run): string {
  return `R${run.run_number} · ${run.run_number === 0 ? "Reference" : "Candidate"}`;
}

type RunFailureDiagnostic = {
  label: string;
  message: string;
};

export function classifyRunFailure(
  statusDetail: string | null | undefined,
): RunFailureDiagnostic | null {
  if (!statusDetail) return null;
  if (
    statusDetail.includes("selected overlay PVP file is missing")
    || statusDetail.includes("selected PVP file is missing")
  ) {
    return {
      label: "Missing runtime PVP",
      message: (
        "The PVP index selected a damper curve that is not packaged in this "
        + "runtime. Fix the runtime data manifest before retrying this overlay."
      ),
    };
  }
  if (statusDetail.includes("inputs unresolved")) {
    return {
      label: "Input resolution",
      message: "The runner could not resolve one or more required engineering inputs.",
    };
  }
  if (statusDetail.includes("spec build failed")) {
    return {
      label: "Spec build",
      message: "The car/platform/tyre/damper specification could not be assembled.",
    };
  }
  if (statusDetail.includes("validate") || statusDetail.includes("waveform")) {
    return {
      label: "Physics validation",
      message: "The physics run reached validation but could not produce a valid waveform/KPI artifact.",
    };
  }
  return null;
}

function describeRunActivity(run: Run): string {
  if (run.status === "pending") {
    return "Queued for virtual rig synthesis. Waiting for a worker to start the run.";
  }
  if (run.status === "running") {
    return (
      "Running virtual rig synthesis: resolving study inputs, integrating the waveform, "
      + "then writing KPI and provenance artifacts."
    );
  }
  if (run.status === "failed") {
    const diagnostic = classifyRunFailure(run.status_detail);
    if (diagnostic) return diagnostic.message;
    return run.status_detail
      ? "Virtual rig synthesis failed. The runner diagnostic is shown below."
      : "Virtual rig synthesis failed, but the runner did not return diagnostic detail.";
  }
  if (run.status === "cancelled") {
    return "Run was cancelled before completion.";
  }
  return "Run completed and artifacts are available.";
}

function runsForActivityPanel(runs: Run[]): Run[] {
  const sorted = [...runs].sort((a, b) => a.run_number - b.run_number);
  return sorted.filter((r) => ["pending", "running", "failed", "cancelled"].includes(r.status));
}

// An objective delta is an "improvement" when it moves the objective in
// its preferred direction: min → more-negative is better, max →
// more-positive is better. "target" objectives have no monotone better
// direction, so a delta is shown neutrally (not coloured as good/bad).
function deltaIsImprovement(
  delta: number,
  direction: "min" | "max" | "target",
): boolean | null {
  if (delta === 0) return null;
  if (direction === "min") return delta < 0;
  if (direction === "max") return delta > 0;
  return null; // "target"
}

// Format an objective delta with an explicit sign (+/-). Small magnitudes
// keep a few sig-figs so adjacent-setup deltas (typ. 0.1–5%) stay legible.
function formatDelta(delta: number): string {
  const sign = delta > 0 ? "+" : delta < 0 ? "−" : "";
  const mag = Math.abs(delta);
  const digits = mag !== 0 && mag < 1 ? 4 : 3;
  return `${sign}${mag.toPrecision(digits)}`;
}

// Wrap-around index step for the run-rail keyboard cursor. delta is +1 (down /
// j) or -1 (up / k); the result stays in [0, count). Exported for tests.
export function nextRunIndex(current: number, count: number, delta: number): number {
  if (count <= 0) return 0;
  return (((current + delta) % count) + count) % count;
}

// True when a keystroke should be left to the focused element rather than
// driving the run rail: text inputs, textareas, selects, and contentEditable
// regions all own their own keys. Exported for tests.
export function isEditableTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return el.isContentEditable === true;
}


export function TestDetailView({
  testId, onClose, onTestUpdated, onDeleted,
}: TestDetailViewProps) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  // Slice 2: consolidated study evaluation (objective + per-run
  // guardrails). Fetched best-effort alongside the Test; a failure
  // leaves it null and the page renders without the eval overlays.
  const [evaluation, setEvaluation] = useState<StudyEvaluation | null>(null);
  const [studyInputs, setStudyInputs] = useState<StudyInputsResponse | null>(null);
  const [studyReport, setStudyReport] = useState<StudyReport | null>(null);
  const [predictBusy, setPredictBusy] = useState(false);
  const [predictError, setPredictError] = useState<string | null>(null);
  // Per-run "cancelling" latch so an Abort button disables after the click.
  const [cancellingRunIds, setCancellingRunIds] = useState<Set<number>>(
    new Set(),
  );
  // Delete confirmation: the first Delete click arms `confirmingDelete`;
  // the confirm click actually calls the backend. Irreversible.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  // editorSource = null means "Damper edit from top-of-page" (blank
  // editor, baseline = R0). When set, opens the editor pre-filled
  // from a specific Run row via the "Duplicate & edit" button.
  const [editorSource, setEditorSource] = useState<Run | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  // Phase 4: per-Run compare selection. Set internally for O(1) toggle;
  // ordered array derived in Run-rail order when handed to children.
  const [selectedRunIds, setSelectedRunIds] = useState<Set<number>>(new Set());
  const [view, setView] = useState<"single" | "compare">("single");
  // Which run's waveform the single-view Results pane shows. null = auto
  // (most-recent-complete). Clicking a run card pins that run so the engineer
  // can inspect R0/R1/R2… waveforms after a study finishes.
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  // Run-rail keyboard cursor (j/k). Distinct from selectedRunId so the cursor
  // can rest on a non-complete run too; null until the first keystroke.
  const [keyboardRunId, setKeyboardRunId] = useState<number | null>(null);
  // T3 Pareto: run_ids on the non-dominated front, lifted from ParetoFrontView's
  // fetch (onMembers) → "◆ front" run-card badges. Empty for single-objective.
  const [paretoFrontRunIds, setParetoFrontRunIds] = useState<Set<number>>(
    new Set(),
  );
  const handleParetoMembers = useCallback((ms: ParetoFrontMember[]) => {
    setParetoFrontRunIds(
      new Set(ms.filter((m) => m.on_front).map((m) => m.run_id)),
    );
  }, []);
  // Keep a ref to the latest test so the polling effect can decide
  // whether to keep polling without re-running on every state set.
  const latestRef = useRef<Test | null>(null);

  const load = useCallback(async () => {
    try {
      const t = await fetchTest(testId);
      latestRef.current = t;
      setState({ kind: "ready", test: t });
      onTestUpdated?.(t);
      // Best-effort evaluation fetch: surface objective/guardrail
      // overlays when available, but a failure here must NOT break the
      // page — fall back to null and continue.
      try {
        setEvaluation(await fetchStudyEvaluation(testId));
      } catch {
        setEvaluation(null);
      }
      // Four-input audit detail (pending until R0 completes). Best-effort.
      try {
        setStudyInputs(await fetchStudyInputs(testId));
      } catch {
        setStudyInputs(null);
      }
      // Post-study report. Best-effort like evaluation/input summaries:
      // missing or pre-feature backends should not break the workbench.
      try {
        setStudyReport(await fetchStudyReport(testId));
      } catch {
        setStudyReport(null);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setState({ kind: "error", message });
    }
  }, [testId, onTestUpdated]);

  // Hard-delete this Test. Guarded behind the inline confirm in the header.
  const confirmDelete = useCallback(async () => {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteTest(testId);
      onDeleted?.();
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : String(e));
      setDeleteBusy(false);  // stay on the confirm so they can retry / cancel
    }
  }, [testId, onDeleted]);

  const saveRename = useCallback(async () => {
    const test = latestRef.current;
    if (!test) return;
    setRenameBusy(true);
    setRenameError(null);
    try {
      const fresh = await updateTest(test.id, {
        name: renameValue.trim() || null,
      });
      latestRef.current = fresh;
      setState({ kind: "ready", test: fresh });
      onTestUpdated?.(fresh);
      setRenaming(false);
    } catch (e) {
      setRenameError(e instanceof Error ? e.message : String(e));
    } finally {
      setRenameBusy(false);
    }
  }, [renameValue, onTestUpdated]);

  // Re-run the baseline. R0 is auto-enqueued on Test creation, so this
  // button is for engineer-initiated re-runs (after the first R0 has
  // completed or failed). Bumping retry_n changes the dedup_key so the
  // server creates a fresh Run row rather than returning the existing
  // baseline back. damper_setting itself stays empty — this is a true
  // re-run of the baseline, not an edit.
  const rerunBaseline = useCallback(async () => {
    const test = latestRef.current;
    if (!test) return;
    const r0 = test.runs.find((r) => r.run_number === 0) ?? null;
    const completedRuns = test.runs.filter((r) => r.run_number > 0).length;
    setPredictBusy(true);
    setPredictError(null);
    try {
      await createRun(test.id, {
        baseline_run_id: r0?.id ?? null,
        damper_setting: {},
        retry_n: completedRuns + 1,
      });
      await load();
    } catch (e) {
      setPredictError(e instanceof Error ? e.message : String(e));
    } finally {
      setPredictBusy(false);
    }
  }, [load]);

  // Retry a failed Run by re-POSTing with a bumped retry counter.
  // The runner ignores the counter; it exists purely to differentiate
  // the dedup key so the engineer gets a fresh run row rather than
  // the existing failed one back.
  const retryRun = useCallback(
    async (failed: Run) => {
      const test = latestRef.current;
      if (!test) return;
      // Carry the failed Run's overlay forward unchanged (so retry
      // means 'try this same edit again', not 'reset to baseline').
      const setting = (failed.damper_setting ?? {}) as Record<string, unknown>;
      // Count existing retry attempts so each Retry click bumps the salt.
      const siblings = test.runs.filter(
        (r) => r.baseline_run_id === failed.baseline_run_id && r.id !== failed.id,
      ).length;
      setPredictBusy(true);
      setPredictError(null);
      try {
        await createRun(test.id, {
          baseline_run_id: failed.baseline_run_id ?? null,
          damper_setting: setting,
          retry_n: siblings + 1,
        });
        await load();
      } catch (e) {
        setPredictError(e instanceof Error ? e.message : String(e));
      } finally {
        setPredictBusy(false);
      }
    },
    [load],
  );

  // Cancel an in-flight run (pending or running). The backend honours cancel
  // mid-solve with ~0.5 s latency; the next poll reflects the terminal status.
  const cancelRunById = useCallback(
    async (id: number) => {
      setCancellingRunIds((prev) => new Set(prev).add(id));
      try {
        await cancelRun(id);
        await load();
      } catch (e) {
        setPredictError(e instanceof Error ? e.message : String(e));
        setCancellingRunIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [load],
  );

  // Re-run the Reference run on current physics (2026-06-11 build):
  // purges R0's artifacts + requeues its job. The remedy when AR start
  // refuses a baseline that predates the current KPI set.
  const rerunReferenceRun = useCallback(async () => {
    const test = latestRef.current;
    if (!test) return;
    setPredictBusy(true);
    setPredictError(null);
    try {
      await rerunReference(test.id);
      await load();
    } catch (e) {
      setPredictError(e instanceof Error ? e.message : String(e));
    } finally {
      setPredictBusy(false);
    }
  }, [load]);

  // Initial load.
  useEffect(() => {
    setState({ kind: "loading" });
    latestRef.current = null;
    setRenaming(false);
    setRenameError(null);
    void load();
  }, [testId, load]);

  // Polling loop while any Run is non-terminal.
  useEffect(() => {
    if (state.kind !== "ready") return;
    // Fast poll while work is in flight; slow poll when idle so a
    // server-side requeue (reference re-run via API, AutoResearch
    // members enqueued by the driver) still surfaces without a manual
    // refresh — a terminal snapshot must not freeze the view forever.
    const interval = hasNonTerminalRun(state.test.runs)
      ? POLL_INTERVAL_MS
      : IDLE_POLL_INTERVAL_MS;
    const handle = window.setInterval(load, interval);
    return () => window.clearInterval(handle);
  }, [state, load]);

  // 1s clock for the elapsed counter on running Run cards. Only ticks
  // while something is in flight — idle studies pay nothing.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const liveRuns = state.kind === "ready" && hasNonTerminalRun(state.test.runs);
  useEffect(() => {
    if (!liveRuns) return;
    setNowMs(Date.now());
    const handle = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(handle);
  }, [liveRuns]);

  // Phase 4: polling-driven selection invalidation. When polling reveals a
  // selected Run is no longer `complete`, drop it from selection. If the
  // remaining set falls below 2 while compare is active, exit compare.
  useEffect(() => {
    if (state.kind !== "ready") return;
    const completeIds = new Set(
      state.test.runs.filter((r) => r.status === "complete").map((r) => r.id),
    );
    let changed = false;
    const next = new Set<number>();
    for (const id of selectedRunIds) {
      if (completeIds.has(id)) next.add(id);
      else changed = true;
    }
    if (changed) setSelectedRunIds(next);
    if (view === "compare" && next.size < 2) setView("single");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Run-rail keyboard navigation. j/k move the cursor down/up the rail
  // (wrapping), c toggles the compare checkbox on the cursor's run, Escape
  // clears the compare selection. Suppressed while a text field is focused or
  // the damper-edit sheet is open, and while a modifier is held (so Cmd/Ctrl+C
  // still copies).
  useEffect(() => {
    if (state.kind !== "ready") return;
    const sorted = [...state.test.runs].sort(
      (a, b) => a.run_number - b.run_number,
    );
    if (sorted.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (editorOpen) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditableTarget(document.activeElement)) return;
      const key = e.key;
      if (key !== "j" && key !== "k" && key !== "c" && key !== "Escape") return;
      const anchorId = keyboardRunId ?? selectedRunId ?? sorted[0].id;
      const found = sorted.findIndex((r) => r.id === anchorId);
      const curIdx = found >= 0 ? found : 0;
      if (key === "j" || key === "k") {
        e.preventDefault();
        // First keystroke reveals the cursor on the current run; subsequent
        // keystrokes step it (so no run is skipped by an implicit start).
        const target =
          keyboardRunId == null
            ? sorted[curIdx]
            : sorted[nextRunIndex(curIdx, sorted.length, key === "j" ? 1 : -1)];
        setKeyboardRunId(target.id);
        // Follow the cursor into Results when the target has a waveform.
        if (target.status === "complete") {
          setSelectedRunId(target.id);
          setView("single");
        }
        const el = document.querySelector(`[data-testid="run-row-${target.id}"]`);
        (el as HTMLElement | null)?.scrollIntoView?.({ block: "nearest" });
      } else if (key === "c") {
        const target = sorted[curIdx];
        if (!target || target.status !== "complete") return;
        e.preventDefault();
        setSelectedRunIds((prev) => {
          const next = new Set(prev);
          if (next.has(target.id)) next.delete(target.id);
          else if (next.size < 4) next.add(target.id);
          return next;
        });
      } else if (key === "Escape") {
        e.preventDefault();
        setSelectedRunIds(new Set());
        setView("single");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, keyboardRunId, selectedRunId, editorOpen]);

  if (state.kind === "loading") {
    return (
      <div className="test-active-wrap">
        <div className="card">
          <p className="muted">Loading test #{testId}…</p>
        </div>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="test-active-wrap">
        <div className="card">
          <p style={{ color: "#ff9a9a" }}>
            Failed to load test #{testId}: {state.message}
          </p>
          <button type="button" className="btn" onClick={load}>
            Retry
          </button>
          <button
            type="button"
            className="btn"
            style={{ marginLeft: "8px" }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const test = state.test;
  // The run actually shown in Results (the pinned selection when complete,
  // else the most-recent-complete default) — used to highlight its card.
  const displayedRunId: number | null = (() => {
    if (selectedRunId != null) {
      const sel = test.runs.find(
        (r) => r.id === selectedRunId && r.status === "complete",
      );
      if (sel) return sel.id;
    }
    const complete = test.runs.filter((r) => r.status === "complete");
    if (complete.length === 0) return null;
    return complete.reduce(
      (b, r) => (r.run_number > b.run_number ? r : b), complete[0],
    ).id;
  })();
  // The (2..4) compare selection in Run-rail (run_number) order, shared by the
  // comparison pane and the FRF overlay table so both read the same run set,
  // first = reference.
  const orderedCompareRunIds = test.runs
    .filter((r) => selectedRunIds.has(r.id))
    .sort((a, b) => a.run_number - b.run_number)
    .map((r) => r.id);
  const headerTitle = test.name?.trim()
    ? `Study ${test.name} · #${test.id}`
    : `Virtual Rig Study #${test.id} · ${test.track_code}`;
  const runActivity = runsForActivityPanel(test.runs);

  return (
    <div className="test-active-wrap">
      <div className="card test-header">
        <div className="study-title-row">
          {!renaming ? (
            <>
              <h3>{headerTitle}</h3>
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  setRenameValue(test.name?.trim() || "");
                  setRenameError(null);
                  setRenaming(true);
                }}
                data-testid="rename-study-button"
              >
                Rename
              </button>
            </>
          ) : (
            <form
              className="study-rename-form"
              onSubmit={(ev) => {
                ev.preventDefault();
                void saveRename();
              }}
            >
              <label htmlFor="study-rename-input" className="sr-only">
                Study name
              </label>
              <input
                id="study-rename-input"
                className="input study-rename-input"
                value={renameValue}
                maxLength={120}
                placeholder={test.source_event ?? test.track_code}
                onChange={(e) => setRenameValue(e.target.value)}
                autoFocus
              />
              <button
                type="submit"
                className="btn primary"
                disabled={renameBusy}
                data-testid="save-rename-button"
              >
                {renameBusy ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="btn"
                disabled={renameBusy}
                onClick={() => {
                  setRenaming(false);
                  setRenameError(null);
                }}
              >
                Cancel
              </button>
            </form>
          )}
        </div>
        <p className="muted">{`Targets: ${describeKpiTargets(test.kpi_targets)}`}</p>
        <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
          <button
            type="button"
            className="btn primary"
            onClick={rerunBaseline}
            disabled={predictBusy || !test.source_event}
            title={
              !test.source_event
                ? "This Test has no source_event — the runner can't resolve inputs"
                : "Re-run the baseline (creates a new Run row)"
            }
            data-testid="predict-button"
          >
            {predictBusy ? "Enqueuing…" : "Re-run reference"}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => setEditorOpen(true)}
            disabled={!test.source_event}
            title={
              !test.source_event
                ? "This Test has no source_event — set one before editing"
                : "Open the damper edit sheet to create a new Run with overlay edits"
            }
            data-testid="open-editor-button"
          >
            Damper edit
          </button>
          <button type="button" className="btn" onClick={load}>
            Refresh
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Close study
          </button>
          {!confirmingDelete ? (
            <button
              type="button"
              className="btn"
              style={{ color: "#ff9a9a" }}
              onClick={() => {
                setDeleteError(null);
                setConfirmingDelete(true);
              }}
              data-testid="delete-test-button"
              title="Permanently delete this test and all its runs"
            >
              Delete
            </button>
          ) : (
            <span style={{ display: "inline-flex", gap: "8px", alignItems: "center" }}>
              <span style={{ fontSize: "12px", color: "#ff9a9a" }}>
                Delete study and all its runs? Can’t be undone.
              </span>
              <button
                type="button"
                className="btn"
                style={{ color: "#ff9a9a", fontWeight: 600 }}
                onClick={confirmDelete}
                disabled={deleteBusy}
                data-testid="confirm-delete-button"
              >
                {deleteBusy ? "Deleting…" : "Delete"}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleteBusy}
                data-testid="cancel-delete-button"
              >
                Cancel
              </button>
            </span>
          )}
        </div>
        {deleteError && (
          <p
            data-testid="delete-error"
            style={{ color: "#ff9a9a", marginTop: "8px", fontSize: "12px" }}
          >
            {deleteError}
          </p>
        )}
        {renameError && (
          <p
            data-testid="rename-error"
            style={{ color: "#ff9a9a", marginTop: "8px", fontSize: "12px" }}
          >
            {renameError}
          </p>
        )}
        {predictError && (
          <p
            data-testid="predict-error"
            style={{ color: "#ff9a9a", marginTop: "8px", fontSize: "12px" }}
          >
            {predictError}
          </p>
        )}
      </div>

      <div className="study-body">
        <StudyDefinitionRail test={test} evaluation={evaluation} inputs={studyInputs} />
        <div className="study-eval">
          <div className="study-ops" data-testid="study-operations">
            <section className="card run-strip-wrap" data-testid="run-rail">
              <h4 style={{ marginTop: 0 }}>Runs</h4>
              {test.runs.length === 0 ? (
                <p className="muted">(no runs yet)</p>
              ) : (
                <ul
                  className="run-strip-list"
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    gap: "8px",
                  }}
                >
                  {[...test.runs].sort((a, b) => a.run_number - b.run_number).map((r) => {
                    // Slice 2: per-run evaluation rollup (guardrails +
                    // objective delta), keyed by stringified run_id.
                    const runEval = evaluation?.runs[String(r.id)] ?? null;
                    const guardrails = runEval?.guardrails ?? null;
                    // Tri-state: a replay run with no recorded truth shows
                    // N/A, not FAIL (nothing was gateable).
                    const grVerdict = guardrails
                      ? guardrailVerdict(guardrails)
                      : null;
                    // Engineering-validity axis (2026-06-11 build) —
                    // separate from the fidelity tri-state. BREACH = the
                    // run's absolute excursion exceeded a physical limit
                    // (travel / tyre envelope); null for pre-build runs.
                    const validityState =
                      runEval?.validity?.overall_state ?? null;
                    const objDir = evaluation?.objective?.direction ?? null;
                    const delta = runEval?.objective_delta_vs_reference ?? null;
                    // Candidate (run_number > 0) objective delta, coloured by
                    // whether it improves the objective per its direction.
                    const showDelta = r.run_number > 0 && delta != null;
                    const improving =
                      showDelta && objDir != null
                        ? deltaIsImprovement(delta as number, objDir)
                        : null;
                    const failureDiagnostic = classifyRunFailure(r.status_detail);
                    return (
                    <li
                      key={r.id}
                      className={`run-row${r.run_number === 0 ? " is-reference" : ""}`}
                      data-testid={`run-row-${r.id}`}
                      data-kbd-cursor={keyboardRunId === r.id ? "true" : undefined}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr auto",
                        gap: "8px",
                        alignItems: "center",
                        padding: "8px",
                        borderRadius: "4px",
                        backgroundColor: "#222",
                        outline:
                          displayedRunId === r.id
                            ? "2px solid #4a90d9"
                            : "none",
                        outlineOffset: "-1px",
                      }}
                    >
                      <input
                        type="checkbox"
                        data-testid={`run-checkbox-${r.id}`}
                        className="run-row-checkbox"
                        disabled={
                          r.status !== "complete" ||
                          (!selectedRunIds.has(r.id) && selectedRunIds.size >= 4)
                        }
                        checked={selectedRunIds.has(r.id)}
                        onChange={() => {
                          setSelectedRunIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(r.id)) next.delete(r.id);
                            else next.add(r.id);
                            return next;
                          });
                        }}
                      />
                      <div
                        role={r.status === "complete" ? "button" : undefined}
                        tabIndex={r.status === "complete" ? 0 : undefined}
                        data-testid={`run-view-${r.id}`}
                        title={
                          r.status === "complete"
                            ? "Show this run's waveform in Results"
                            : "Run not complete — no waveform yet"
                        }
                        onClick={
                          r.status === "complete"
                            ? () => {
                                setSelectedRunId(r.id);
                                setView("single");
                              }
                            : undefined
                        }
                        onKeyDown={
                          r.status === "complete"
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setSelectedRunId(r.id);
                                  setView("single");
                                }
                              }
                            : undefined
                        }
                        style={{
                          cursor:
                            r.status === "complete" ? "pointer" : "default",
                        }}
                      >
                        <strong>
                          R{r.run_number} · {r.run_number === 0 ? "Reference" : "Candidate"}
                          {displayedRunId === r.id && (
                            <span
                              style={{
                                marginLeft: "6px",
                                fontSize: "10px",
                                color: "#4a90d9",
                                fontWeight: 600,
                              }}
                            >
                              ● shown
                            </span>
                          )}
                          {paretoFrontRunIds.has(r.id) && (
                            <span
                              title="On the Pareto front (non-dominated)"
                              style={{
                                marginLeft: "6px",
                                fontSize: "10px",
                                color: "#4a90d9",
                                fontWeight: 600,
                              }}
                            >
                              ◆ front
                            </span>
                          )}
                        </strong>
                        <span
                          className="muted"
                          style={{ fontSize: "11px", marginLeft: "8px" }}
                        >
                          {r.source}
                        </span>
                        <div
                          className="run-intent"
                          data-testid={`run-intent-${r.id}`}
                        >
                          {describeRunIntent(r)}
                        </div>
                        {runLiveLine(r, nowMs) && (
                          <div
                            className="run-live-line"
                            data-testid={`run-live-${r.id}`}
                          >
                            {runLiveLine(r, nowMs)}
                          </div>
                        )}
                        <RunSimProgress run={r} nowMs={nowMs} />
                        {(r.status === "running" || r.status === "pending") && (
                          <button
                            type="button"
                            className="btn run-abort-btn"
                            style={{ marginTop: "6px", fontSize: "11px", padding: "2px 8px" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              void cancelRunById(r.id);
                            }}
                            disabled={cancellingRunIds.has(r.id)}
                            data-testid={`abort-${r.id}`}
                            title="Cancel this run"
                          >
                            {cancellingRunIds.has(r.id) ? "cancelling…" : "Abort run"}
                          </button>
                        )}
                        {(guardrails || showDelta || validityState) && (
                          <div
                            className="run-eval"
                            data-testid={`run-eval-${r.id}`}
                            style={{
                              display: "flex",
                              gap: "8px",
                              alignItems: "center",
                              marginTop: "4px",
                            }}
                          >
                            {guardrails && grVerdict && (
                              <span
                                className={`run-guardrail-chip ${
                                  grVerdict === "PASS"
                                    ? "ok"
                                    : grVerdict === "FAIL"
                                      ? "warn"
                                      : "na"
                                }`}
                                data-testid={`run-guardrail-${r.id}`}
                                title="Fidelity (sim vs recorded truth)"
                              >
                                {grVerdict === "NOT_EVALUABLE" ? "N/A" : grVerdict}
                              </span>
                            )}
                            {validityState && validityState !== "NOT_EVALUABLE" && (
                              <span
                                className={`run-guardrail-chip ${
                                  validityState === "OK" ? "ok" : "na"
                                }`}
                                data-testid={`run-validity-${r.id}`}
                                title={
                                  validityState === "OK"
                                    ? "Within the characterised envelope "
                                      + "(damper travel + tyre load + platform sink)."
                                    : "Absolute envelope state — informational, not "
                                      + "a pass/fail. The model exceeded a characterised "
                                      + "limit (typically front damper travel on a "
                                      + "replay lap, where the vertical rig has no ground "
                                      + "plane). AutoResearch scores candidates RELATIVE "
                                      + "to the Reference, so this does not gate the "
                                      + "study. See Validity detail."
                                }
                              >
                                {validityState === "OK"
                                  ? "VALID"
                                  : "outside envelope"}
                              </span>
                            )}
                            {showDelta && (
                              <span
                                className={`run-delta-chip${
                                  improving === true
                                    ? " good"
                                    : improving === false
                                      ? " bad"
                                      : ""
                                }`}
                                data-testid={`run-delta-${r.id}`}
                                title={
                                  evaluation?.objective?.display_name
                                    ? `Δ vs reference · ${evaluation.objective.display_name}`
                                    : "Δ vs reference"
                                }
                              >
                                {formatDelta(delta as number)}
                              </span>
                            )}
                          </div>
                        )}
                        {runEval?.validity && (
                          <RunValidityDetail
                            runId={r.id}
                            validity={runEval.validity}
                            kpiScores={
                              (r.kpi_scores as Record<string, unknown> | null)
                              ?? null
                            }
                          />
                        )}
                        <div className="muted" style={{ fontSize: "11px" }}>
                          {r.artifacts.length} artifact
                          {r.artifacts.length === 1 ? "" : "s"}
                        </div>
                        {failureDiagnostic ? (
                          <div
                            className="run-failure-classification"
                            data-testid={`run-failure-classification-${r.id}`}
                          >
                            <strong>{failureDiagnostic.label}</strong>
                            <span>{failureDiagnostic.message}</span>
                          </div>
                        ) : null}
                        {r.status_detail ? (
                          <div
                            // While running, status_detail is the live
                            // stage line (the runner stamps it) — style
                            // it as activity, not as the failure block.
                            className={
                              r.status === "running"
                                ? "run-status-detail run-status-detail--live"
                                : "run-status-detail"
                            }
                            data-testid={`run-status-detail-${r.id}`}
                          >
                            {r.status_detail}
                          </div>
                        ) : null}
                        {r.status === "failed" && (
                          <button
                            type="button"
                            className="btn"
                            style={{ marginTop: "6px", fontSize: "11px", padding: "2px 8px" }}
                            onClick={() => retryRun(r)}
                            disabled={predictBusy}
                            data-testid={`retry-${r.id}`}
                          >
                            Retry
                          </button>
                        )}
                        {r.run_number === 0
                          && ["complete", "failed", "cancelled"].includes(r.status)
                          && (
                          <button
                            type="button"
                            className="btn"
                            style={{ marginTop: "6px", fontSize: "11px", padding: "2px 8px" }}
                            onClick={() => void rerunReferenceRun()}
                            disabled={predictBusy}
                            data-testid={`rerun-reference-${r.id}`}
                            title={
                              "Recompute the Reference run on current "
                              + "physics (purges its artifacts and re-queues "
                              + "it). Needed when AutoResearch reports the "
                              + "baseline predates the current KPI set."
                            }
                          >
                            Re-run reference
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn"
                          style={{ marginTop: "6px", fontSize: "11px", padding: "2px 8px" }}
                          onClick={() => {
                            setEditorSource(r);
                            setEditorOpen(true);
                          }}
                          disabled={!test.source_event}
                          data-testid={`dup-edit-${r.id}`}
                          title={
                            r.run_number === 0
                              ? "Open the editor with an empty form (R0 has no overlay)"
                              : "Open the editor pre-filled with this Run's overlay; submit creates a new Run"
                          }
                        >
                          Duplicate & edit
                        </button>
                      </div>
                      <StatusChip status={r.status} />
                    </li>
                    );
                  })}
                </ul>
              )}
              {selectedRunIds.size > 0 ? (
                <div className="run-rail-footer">
                  <span>{selectedRunIds.size} selected</span>
                  <button
                    type="button"
                    className="btn"
                    disabled={selectedRunIds.size < 2}
                    onClick={() => setView("compare")}
                  >
                    Compare selected
                  </button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setSelectedRunIds(new Set());
                      setView("single");
                    }}
                  >
                    Clear
                  </button>
                </div>
              ) : null}
              {test.runs.length > 0 ? (
                <div
                  className="run-rail-kbd-hint"
                  data-testid="run-rail-kbd-hint"
                >
                  <kbd>j</kbd> / <kbd>k</kbd> move · <kbd>c</kbd> compare ·{" "}
                  <kbd>Esc</kbd> clear
                </div>
              ) : null}
              {runActivity.length > 0 ? (
                <div className="run-activity-panel" data-testid="run-activity-panel">
                  <div className="run-activity-header">
                    <span>Run activity</span>
                    <span className="muted">updates every {POLL_INTERVAL_MS / 1000}s</span>
                  </div>
                  <ul className="run-activity-list">
                    {runActivity.map((r) => {
                      const failureDiagnostic = classifyRunFailure(r.status_detail);
                      return (
                        <li
                          key={r.id}
                          className={`run-activity-item is-${r.status}`}
                          data-testid={`run-activity-${r.id}`}
                        >
                          <div className="run-activity-title">
                            <strong>{describeRunRole(r)}</strong>
                            <StatusChip status={r.status} />
                          </div>
                          <div className="run-activity-message">
                            {describeRunActivity(r)}
                          </div>
                          {runLiveLine(r, nowMs) && (
                            <div
                              className="run-live-line"
                              data-testid={`run-activity-live-${r.id}`}
                            >
                              {runLiveLine(r, nowMs)}
                            </div>
                          )}
                          {failureDiagnostic ? (
                            <div
                              className="run-failure-classification"
                              data-testid={`run-activity-classification-${r.id}`}
                            >
                              <strong>{failureDiagnostic.label}</strong>
                              <span>{failureDiagnostic.message}</span>
                            </div>
                          ) : null}
                          {r.status_detail ? (
                            <pre
                              className={
                                r.status === "running"
                                  ? "run-activity-detail run-activity-detail--live"
                                  : "run-activity-detail"
                              }
                            >
                              {r.status_detail}
                            </pre>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </section>

            <div className="study-ar-panel" data-testid="autoresearch-dock">
              <AutoResearchPanel test={test} onRunCreated={load} />
            </div>
          </div>

          <LiveStudyPanel test={test} evaluation={evaluation} />

          <StudyReportPanel report={studyReport} />

          {studyReport?.session && (
            <ParetoFrontView
              sessionId={studyReport.session.id}
              live={studyReport.session.state === "running"}
              onMembers={handleParetoMembers}
            />
          )}

        {view === "compare" ? (
          <ComparisonPane
            test={test}
            selectedRunIds={orderedCompareRunIds}
            onClear={() => {
              setSelectedRunIds(new Set());
              setView("single");
            }}
          />
        ) : (
          <ResultsPane test={test} selectedRunId={selectedRunId} />
        )}
        <FrfPanel
          test={test}
          mode={view}
          singleRunId={displayedRunId}
          compareRunIds={orderedCompareRunIds}
        />
        </div>
      </div>

      {editorOpen && (() => {
        // Resolve baseline_run_id from the source Run if provided:
        //   - R0 → baseline = R0.id (R0 IS the baseline).
        //   - R1+ → baseline = source.baseline_run_id (chain to the
        //     same baseline as siblings; don't make R2 a child of R1).
        // No source (top-of-page Damper edit) → baseline = R0 (legacy
        // behaviour preserved).
        const r0 = test.runs.find((r) => r.run_number === 0) ?? null;
        const baselineId = editorSource
          ? (editorSource.run_number === 0
              ? editorSource.id
              : editorSource.baseline_run_id ?? r0?.id ?? null)
          : (r0?.id ?? null);
        return (
          <DamperEditSheet
            testId={test.id}
            baselineRunId={baselineId}
            initialOverlay={
              editorSource
                ? (editorSource.damper_setting as DamperSettingPayload | null)
                : null
            }
            sourceRunLabel={
              editorSource ? `R${editorSource.run_number}` : null
            }
            onClose={() => {
              setEditorOpen(false);
              setEditorSource(null);
            }}
            onRunCreated={() => {
              setEditorOpen(false);
              setEditorSource(null);
              void load();
            }}
          />
        );
      })()}
    </div>
  );
}
